import type {
  ImageContent,
  MediaVideo,
  TextContent,
  Video,
} from "@homeinstead/schemas";
import type { VideoObjectData } from "~www/types/video-object";

import { getSanityFileUrl } from "~www/lib/sanity/get-sanity-file-url";
import { urlFor } from "~www/lib/sanity/sanity-images";
import { secondsToIso8601Duration } from "~www/lib/schema";
import {
  buildHostedContentUrl,
  buildHostedEmbedUrl,
  buildLegacyEmbedUrl,
} from "./resolve-video-source-urls";

type ImageContentLike = Pick<ImageContent, "image"> | null | undefined;

type MediaVideoBlock = Omit<MediaVideo, "_type"> & {
  _key?: string;
};

type LegacyVideoBlock = Omit<Video, "_type" | "name"> & {
  name?: string;
};

type SanityImageLike =
  | {
      asset?: { _ref?: string };
    }
  | null
  | undefined;

function resolveImageContentUrl(
  imageContent: ImageContentLike,
): string | undefined {
  const assetReference = imageContent?.image?.asset?._ref;

  if (!assetReference || !imageContent?.image?.asset) {
    return undefined;
  }

  return urlFor(imageContent.image.asset).url();
}

function resolveSanityImageUrl(image: SanityImageLike): string | undefined {
  if (!image?.asset?._ref) {
    return undefined;
  }

  return urlFor(image).url();
}

function extractPlainTextFromPortableText(
  blocks: TextContent["text"] | undefined,
): string | undefined {
  if (!Array.isArray(blocks)) {
    return undefined;
  }

  const parts: string[] = [];

  for (const block of blocks) {
    if (block._type !== "block" || !Array.isArray(block.children)) {
      continue;
    }

    for (const child of block.children) {
      if (child._type === "span" && typeof child.text === "string") {
        parts.push(child.text);
      }
    }
  }

  const plainText = parts.join(" ").trim();
  return plainText || undefined;
}

function buildVideoObjectMetadata({
  description,
  durationSeconds,
  name,
  regionsAllowed,
  thumbnailUrl,
  uploadDate,
}: {
  description?: string | null;
  durationSeconds?: number | null;
  name?: string | null;
  regionsAllowed?: string[] | null;
  thumbnailUrl?: string;
  uploadDate?: string | null;
}): Pick<
  VideoObjectData,
  | "description"
  | "duration"
  | "name"
  | "regionsAllowed"
  | "thumbnailUrl"
  | "uploadDate"
> | null {
  const trimmedName = name?.trim();

  if (!trimmedName) {
    return null;
  }

  const resolvedThumbnailUrl = thumbnailUrl ? [thumbnailUrl] : undefined;

  return {
    description: description?.trim() || undefined,
    duration:
      typeof durationSeconds === "number" && durationSeconds > 0
        ? secondsToIso8601Duration(durationSeconds)
        : undefined,
    name: trimmedName,
    regionsAllowed: regionsAllowed?.length ? regionsAllowed : undefined,
    thumbnailUrl: resolvedThumbnailUrl,
    uploadDate: uploadDate || undefined,
  };
}

function buildVideoObjectFromMediaVideo(
  block: MediaVideoBlock,
): VideoObjectData | null {
  const thumbnailUrl = resolveSanityImageUrl(block.poster);
  const metadata = buildVideoObjectMetadata({
    description: block.description,
    durationSeconds: block.durationSeconds,
    name: block.title?.trim() || undefined,
    regionsAllowed: block.regionsAllowed,
    thumbnailUrl,
    uploadDate: block.uploadDate,
  });

  if (!metadata) {
    return null;
  }

  if (block.sourceType === "file") {
    const contentUrl = getSanityFileUrl(block.videoFile);

    if (!contentUrl) {
      return null;
    }

    return {
      ...metadata,
      contentUrl,
      embedUrl: contentUrl,
    };
  }

  if (!block.videoId) {
    return null;
  }

  return {
    ...metadata,
    contentUrl: buildHostedContentUrl(block.sourceType, block.videoId),
    embedUrl: buildHostedEmbedUrl(block.sourceType, block.videoId),
  };
}

function buildVideoObjectFromLegacyVideo(
  block: LegacyVideoBlock,
): VideoObjectData | null {
  if (!block.source) {
    return null;
  }

  const thumbnailUrl = resolveImageContentUrl(block.thumbnail);
  const metadata = buildVideoObjectMetadata({
    description: block.description,
    durationSeconds: block.durationSeconds,
    name:
      block.name ??
      extractPlainTextFromPortableText(block.title?.text) ??
      block.source,
    regionsAllowed: block.regionsAllowed,
    thumbnailUrl,
    uploadDate: block.uploadDate,
  });

  if (!metadata) {
    return null;
  }

  return {
    ...metadata,
    contentUrl: block.source,
    embedUrl: buildLegacyEmbedUrl(block.source),
  };
}

export {
  buildVideoObjectFromLegacyVideo,
  buildVideoObjectFromMediaVideo,
  extractPlainTextFromPortableText,
};
